import { Component } from '@theme/component';

/**
 * Orchard custom navigation drawer with expand-right submenu panels.
 *
 * Desktop: hamburger opens a left nav panel; hovering a category shows
 * its child links in a right panel.
 * Mobile: clicking a category slides its submenu in as an overlay with
 * a back button, matching live Dawn.
 *
 * Event wiring is declarative — the Liquid template uses `on:click="/method"`
 * attributes, which Horizon's event delegation system routes to the methods
 * defined here.
 *
 * @extends Component
 */
export class OrchardDrawerComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    /* mouseenter isn't in Horizon's delegated event list, so it still
       needs addEventListener. Click/back/toggle are handled via on: attributes. */
    this.querySelectorAll('[data-has-submenu]').forEach((el) => {
      el.addEventListener('mouseenter', this.#onMenuHover, { signal });
    });

    /* HOR-47: close the drawer when a click-menu dropdown (e.g. desktop
       About) opens so the two menus don't overlap. Capture phase catches
       the toggle event regardless of bubble-support discrepancies. */
    document.addEventListener('toggle', this.#onClickMenuToggle, { signal, capture: true });

    /* Click-away: close the drawer when a pointer lands outside its visible
       surfaces, so the menu behaves like a dropdown. pointerdown (not click)
       fires before navigation and feels snappy. */
    document.addEventListener('pointerdown', this.#onOutsideClick, { signal });

    /* Keep --orchard-drawer-panel-offset in sync with the actual y-coord
       where the panel starts (i.e. our host element's bottom). The panel
       is `top: 100%` of this host, so its height needs to be
       (visible viewport) - (this offset). We can't hardcode the offset
       because it includes the announcement bar (which may or may not be
       present, and varies in height). */
    this.#updatePanelOffset();
    window.addEventListener('resize', this.#updatePanelOffset, { signal });
    window.addEventListener('scroll', this.#updatePanelOffset, { signal, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /* Public (declarative) handlers — invoked via on:click="/methodName" */

  toggle() {
    if (this.isOpen) {
      this.removeAttribute('open');
      this.closePanel();
    } else {
      /* Re-measure right before open in case the page state changed (e.g.
         user scrolled and an announcement bar collapsed) since the last
         resize/scroll event fired. */
      this.#updatePanelOffset();
      this.setAttribute('open', '');
    }
  }

  /**
   * Close the active submenu without closing the drawer itself.
   */
  closePanel() {
    this.querySelectorAll('.orchard-drawer__child-panel').forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = true;
    });
    this.querySelector('.orchard-drawer__close-strip')?.removeAttribute('data-active');
    this.querySelector('.orchard-drawer__wrapper')?.removeAttribute('data-panel-open');
  }

  /**
   * Click handler on menu items with submenus — mobile-safe alternative to hover.
   * @param {Event} event
   */
  onMenuClick(event) {
    const target = /** @type {HTMLElement} */ (event.currentTarget);
    if (!target.dataset.submenuTarget) return;
    event.preventDefault();
    this.#showSubmenu(target.dataset.submenuTarget);
  }

  /**
   * Closes the whole drawer when a pointerdown lands outside its visible
   * surfaces. The white nav is always content; the secondary counts as
   * content only while a submenu is open; the trigger and close-strip manage
   * their own state. Everything else — the empty backdrop, the page behind,
   * the header — closes the drawer.
   * @param {Event} event
   */
  #onOutsideClick = (event) => {
    if (!this.isOpen) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    /* Trigger toggles itself. */
    if (target.closest('.orchard-drawer__trigger')) return;
    /* Close-strip: the X icon collapses just the second level; clicking
       elsewhere on the teal strip reads as backdrop and closes the whole drawer. */
    if (target.closest('.orchard-drawer__close-strip')) {
      if (target.closest('svg')) this.closePanel();
      else this.toggle();
      return;
    }
    /* The white menu column is always interactive content. */
    if (target.closest('.orchard-drawer__nav')) return;
    /* Keep the drawer open only while clicking the actual submenu CONTENT (the
       visible child-panel). Clicking the empty backdrop area of the secondary —
       even with a second level expanded — counts as clicking away and closes
       the whole drawer (both levels), not just the submenu. (A hidden
       child-panel is display:none so it can never be the pointer target, which
       is why no "panel open" check is needed here.) */
    if (target.closest('.orchard-drawer__child-panel')) return;
    this.toggle();
  };

  /* Private helpers */

  /**
   * Write the drawer panel's actual top-y as a CSS variable so the panel can
   * compute its height as (100dvh - this offset) — fixes the footer being
   * clipped on short viewports because the panel was sized as if it only had
   * to clear the trigger (60px) when it actually clears the announcement bar
   * too (~116px).
   *
   * We measure the trigger button's `offsetParent` (the header row that
   * establishes the panel's containing block) rather than the host
   * <orchard-drawer> (display: contents, zero rect) or the panel itself
   * (display: none until opened, so offsetParent would be null).
   */
  #updatePanelOffset = () => {
    const trigger = this.querySelector('.orchard-drawer__trigger');
    const anchor = trigger instanceof HTMLElement ? trigger.offsetParent : null;
    if (!(anchor instanceof Element)) return;
    const y = anchor.getBoundingClientRect().bottom;
    if (!Number.isFinite(y) || y <= 0) return;
    this.style.setProperty('--orchard-drawer-panel-offset', `${Math.round(y)}px`);
  };

  /**
   * Close the drawer when a sibling click-menu (e.g. desktop About) opens,
   * so the two menus don't overlap.
   * @param {Event} event
   */
  #onClickMenuToggle = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    if (
      t instanceof HTMLDetailsElement &&
      t.matches('details[data-orchard-click-menu]') &&
      t.open &&
      this.isOpen
    ) {
      this.toggle();
    }
  };

  /**
   * @param {Event} event
   */
  #onMenuHover = (event) => {
    const target = /** @type {HTMLElement} */ (event.currentTarget);
    const menuId = target.dataset.submenuTarget;
    if (!menuId) return;
    this.#showSubmenu(menuId);
  };

  /** @param {string} menuId */
  #showSubmenu(menuId) {
    this.querySelectorAll('.orchard-drawer__child-panel').forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = true;
    });
    const target = /** @type {HTMLElement | null} */ (this.querySelector(`[data-submenu-id="${menuId}"]`));
    if (target) {
      target.hidden = false;
      this.querySelector('.orchard-drawer__close-strip')?.setAttribute('data-active', '');
      this.querySelector('.orchard-drawer__wrapper')?.setAttribute('data-panel-open', '');
    }
  }
}

if (!customElements.get('orchard-drawer')) {
  customElements.define('orchard-drawer', OrchardDrawerComponent);
}
